import { isOnPrem } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { OpenFeature } from '@openfeature/react-sdk';
import { GeneralError, InMemoryProvider, type JsonValue } from '@openfeature/web-sdk';
import { useEffect } from 'react';

import { FEATURE_FLAG_DOMAIN } from './constants';

interface FeatureFlagBootstrapResponse {
  flags: Record<string, JsonValue>;
}

const FETCH_TIMEOUT_MS = 5_000;
const RETRY_BACKOFF_MS = [1_000, 2_000, 4_000];
const RECOVERY_RETRY_MS = 60_000;

/**
 * Wrap each resolved value as a single-variant flag — the bundle was already
 * evaluated server-side, so there is no client-side targeting to model;
 * `disabled: false` makes the lone variant the resolved value.
 */
function buildProvider(flags: Record<string, JsonValue>): InMemoryProvider {
  const config: ConstructorParameters<typeof InMemoryProvider>[0] = {};
  for (const [key, value] of Object.entries(flags)) {
    config[key] = {
      disabled: false,
      variants: { default: value },
      defaultVariant: 'default',
    };
  }
  return new InMemoryProvider(config);
}

/**
 * Installing this lands the domain at ERROR — a settled status readiness
 * gates treat as resolved — instead of leaving it wedged at NOT_READY, which
 * never resolves and would block consumers that wait on flag resolution.
 */
class BundleUnavailableProvider extends InMemoryProvider {
  constructor(private readonly reason: string) {
    super({});
  }

  initialize(): Promise<void> {
    return Promise.reject(new GeneralError(this.reason));
  }
}

async function installRealProvider(signal: AbortSignal): Promise<void> {
  const { data } = await scribeHttpV2.get<FeatureFlagBootstrapResponse>(
    '/feature-flag/all',
    { signal, timeout: FETCH_TIMEOUT_MS }
  );
  await OpenFeature.setProviderAndWait(FEATURE_FLAG_DOMAIN, buildProvider(data.flags));
}

async function installRealProviderWithRetry(signal: AbortSignal): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      await installRealProvider(signal);
      return;
    } catch (error) {
      const backoffMs = RETRY_BACKOFF_MS[attempt];
      if (signal.aborted || backoffMs === undefined) throw error;
      await new Promise<void>((resolve) => {
        const onAbort = (): void => {
          clearTimeout(timeoutId);
          resolve();
        };
        const timeoutId = setTimeout(() => {
          signal.removeEventListener('abort', onAbort);
          resolve();
        }, backoffMs);
        signal.addEventListener('abort', onAbort, { once: true });
      });
      if (signal.aborted) throw error;
    }
  }
}

async function installBundleUnavailableProvider(cause: unknown): Promise<void> {
  try {
    await OpenFeature.setProviderAndWait(
      FEATURE_FLAG_DOMAIN,
      new BundleUnavailableProvider(`on-prem flag bundle unavailable: ${String(cause)}`)
    );
  } catch {
    // Expected: the provider's initialize() rejects by design — the rejection
    // is what drives the SDK's NOT_READY → ERROR transition.
  }
}

interface RecoveryHandle {
  timeoutId?: ReturnType<typeof setTimeout>;
}

function scheduleRecovery(
  handle: RecoveryHandle,
  signal: AbortSignal,
  failedPolls = 0
): void {
  handle.timeoutId = setTimeout(() => {
    installRealProvider(signal)
      .then(() => logger.info('[flags] on-prem flag bundle recovered'))
      .catch((error: unknown) => {
        if (signal.aborted) return;
        const polls = failedPolls + 1;
        // Surface a long outage without logging every 60s poll.
        if (polls % 10 === 0) {
          logger.warn('[flags] on-prem flag bundle still unavailable', {
            polls,
            error: String(error),
          });
        }
        scheduleRecovery(handle, signal, polls);
      });
  }, RECOVERY_RETRY_MS);
}

async function syncOnPremFlagBundle(
  handle: RecoveryHandle,
  signal: AbortSignal
): Promise<void> {
  try {
    await installRealProviderWithRetry(signal);
  } catch (error) {
    if (signal.aborted) return;
    logger.warn('[flags] failed to seed on-prem flag bundle; using code defaults', {
      error: String(error),
    });
    await installBundleUnavailableProvider(error);
    if (signal.aborted) return;
    scheduleRecovery(handle, signal);
  }
}

/**
 * On-prem only: seed OpenFeature from the backend's whole-bundle evaluation.
 *
 * In an airgap the frontend can't reach Flagsmith, so `_app` never registers a
 * provider and every `use*FlagValue` falls back to its code default. We install
 * the backend's in-process bundle evaluation (`GET /feature-flag/all`, backed
 * by `LocalBundleProvider`) as an `InMemoryProvider` at app load.
 *
 * Deliberately not gated on authentication: like PostHog serving flags before
 * `identify()`, flags must be available on pre-login screens too. The on-prem
 * bundle carries no per-user targeting (single org), so one context-free fetch
 * is correct for every user — there is nothing to re-evaluate on login.
 *
 * A failed fetch is retried with backoff; once retries are exhausted the
 * domain is deliberately landed at ERROR (code defaults stand, readiness
 * gates resolve) and a slow background retry swaps in the real bundle when
 * the backend recovers.
 *
 * No-op on cloud — `isOnPrem` is build-time-folded, so the body is
 * dead-code-eliminated there.
 */
export function OnPremFlagBundleSync(): null {
  useEffect(() => {
    if (!isOnPrem) return;
    // The abort-on-cleanup contract replaces the old once-per-mount ref guard:
    // StrictMode's first effect run is cancelled before the second starts.
    const controller = new AbortController();
    const handle: RecoveryHandle = {};
    void syncOnPremFlagBundle(handle, controller.signal);
    return () => {
      controller.abort();
      clearTimeout(handle.timeoutId);
    };
  }, []);

  return null;
}
